import type { NavigationItem } from "@/components/ui/navigation/types";

/** A single jump-to destination offered by the command palette. */
export interface PaletteDestination {
  id: string;
  /** Leaf name, e.g. "Deals". */
  name: string;
  /** Ancestors joined for context, e.g. "Sales › Records". */
  path: string;
  href: string;
  /** Lowercased haystack used for matching (name + path + href). */
  searchText: string;
}

/**
 * Flatten the role-filtered navigation tree into palette destinations.
 *
 * Reusing the nav tree — rather than a second hardcoded route list — means the
 * palette can never offer a page the current user is not allowed to see, and it
 * picks up new nav entries for free.
 *
 * Only LEAVES are offered: a group whose `href` merely points at its first
 * visible child would otherwise appear twice under different names.
 */
export function flattenNavigationForPalette(
  items: readonly NavigationItem[]
): PaletteDestination[] {
  const destinations: PaletteDestination[] = [];
  const seenHrefs = new Set<string>();

  function walk(item: NavigationItem, ancestors: string[]): void {
    const hasChildren = Boolean(item.children && item.children.length > 0);

    if (hasChildren) {
      for (const child of item.children ?? []) {
        walk(child, [...ancestors, item.name]);
      }
      return;
    }

    if (!item.href || item.visible === false) return;
    // The same destination can be reachable from two branches; keep the first.
    if (seenHrefs.has(item.href)) return;
    seenHrefs.add(item.href);

    const path = ancestors.join(" › ");
    destinations.push({
      id: item.id,
      name: item.name,
      path,
      href: item.href,
      searchText: `${item.name} ${path} ${item.href}`.toLowerCase(),
    });
  }

  for (const item of items) {
    walk(item, []);
  }

  return destinations;
}

/**
 * Rank destinations against a query. Word-prefix matches beat mid-word ones so
 * typing "deal" surfaces "Deals" above "Deal Contracts".
 */
export function filterDestinations(
  destinations: readonly PaletteDestination[],
  query: string,
  limit = 8
): PaletteDestination[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return destinations.slice(0, limit);

  const scored: Array<{ destination: PaletteDestination; score: number }> = [];
  for (const destination of destinations) {
    const name = destination.name.toLowerCase();
    let score: number;
    if (name === needle) score = 0;
    else if (name.startsWith(needle)) score = 1;
    else if (new RegExp(`\\b${escapeRegExp(needle)}`).test(name)) score = 2;
    else if (name.includes(needle)) score = 3;
    else if (destination.searchText.includes(needle)) score = 4;
    else continue;
    scored.push({ destination, score });
  }

  scored.sort((a, b) => a.score - b.score || a.destination.name.localeCompare(b.destination.name));
  return scored.slice(0, limit).map((entry) => entry.destination);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
